import { useState } from 'react'
import { GrowerAreaMap } from '../area-map/grower-area-map'
import { AreaCoordTable } from '../area-map/area-coord-table'
import { normalizeGeo } from '../area-map/use-area-polygon'
import { AreaCameraPanel } from '../area-cameras/area-camera-panel'
import { AREA_VIEW_H, AreaViewToggle } from '../area-cameras/area-view-toggle'
import { GrowerAttachmentsField } from '../attachments/grower-attachments-field'
import { useReadonlyAttachments } from '../attachments/use-readonly-attachments'
import { ReadonlyField } from './readonly-field'
import type { AreaView } from '../area-cameras/area-view-toggle'
import type { AttachmentsFieldDraft } from '../attachments/grower-attachments-field'
import type { AreaMapItem } from '@/lib/api/grower-area-map-api'
import type { GrowerArea, GrowerFileRecord } from '@/lib/api/growers-api'
import { Label } from '@/components/ui/label'
import { useGrowerAreaCameras } from '@/hooks/use-cameras-query'
import { m } from '@/lib/paraglide/messages'
import { twJoin } from '@/lib/utils'

type Props = {
    areas: GrowerArea[]
    /** Hộ đang xem — cần để dựng link tải giấy tờ đất của từng vùng. */
    growerId?: string
    /** Tên hộ cho nhãn các thửa khác của hộ trên bản đồ. */
    growerName?: string
    growerNickname?: string | null
    /** Toàn bộ giấy tờ của hộ; mỗi tab vùng tự lọc phần của mình. */
    files?: GrowerFileRecord[]
    scope?: 'household' | 'admin'
}

/**
 * Vùng trồng ở chế độ chỉ xem: vẫn là tab từng vùng với bản đồ và camera như
 * màn sửa, nhưng thông tin vùng hiện dạng chữ, không có ô nhập, không có nút
 * thêm/xoá vùng.
 *
 * Giấy tờ đất hiện ngay trong tab của vùng giữ nó — cùng chỗ với màn sửa, để
 * người xem không phải đoán bìa đỏ nào thuộc vùng nào.
 */
export function GrowerReadonlyAreas({
    areas,
    growerId,
    growerName,
    growerNickname,
    files,
    scope = 'household',
}: Props) {
    const [active, setActive] = useState(0)
    const idx = Math.min(active, Math.max(areas.length - 1, 0))
    const area = areas[idx]
    // Hook phải đứng trước nhánh trả sớm bên dưới. Chỉ dựng cho vùng đang mở:
    // tab khác không render nên không cần danh sách của chúng.
    const landAttachments = useReadonlyAttachments(
        growerId ?? '',
        growerId ? files : undefined,
        'land',
        scope,
        area?.id,
    )

    if (areas.length === 0 || !area) {
        return (
            <p className="text-sm text-muted-foreground">
                {m.care_log_parcels_empty()}
            </p>
        )
    }

    return (
        <div>
            <div className="mb-4 flex flex-wrap items-center gap-2">
                {areas.map((a, i) => (
                    <button
                        key={a.id ?? a.code ?? i}
                        type="button"
                        onClick={() => setActive(i)}
                        aria-pressed={i === idx}
                        className={twJoin(
                            'inline-flex h-9 items-center rounded-md border px-3.5 text-sm font-semibold',
                            i === idx
                                ? 'border-primary bg-accent text-accent-foreground'
                                : 'bg-card',
                        )}
                    >
                        {a.name?.trim() ||
                            m.grower_area_tab_fallback({ n: i + 1 })}
                    </button>
                ))}
            </div>

            <AreaPanel
                area={area}
                landAttachments={growerId ? landAttachments : undefined}
                neighbors={{
                    growerId,
                    excludeAreaIds: area.id ? [area.id] : [],
                    // Thửa khác của hộ vẽ làm nền từ dữ liệu đã tải (bản DB
                    // bị ẩn theo growerId nên phải cung cấp lại ở đây).
                    siblings: areas.flatMap((a, i) =>
                        i === idx || !a.id
                            ? []
                            : [
                                  {
                                      area_id: a.id,
                                      code: a.code ?? '',
                                      name:
                                          a.name?.trim() ||
                                          m.grower_area_tab_fallback({
                                              n: i + 1,
                                          }),
                                      acreage: a.acreage,
                                      grower_id: growerId ?? 'draft',
                                      grower_name: growerName ?? '',
                                      grower_nickname: growerNickname ?? null,
                                      paths: normalizeGeo(a.paths, a.markers)
                                          .paths,
                                  },
                              ],
                    ),
                }}
            />
        </div>
    )
}

function AreaPanel({
    area,
    landAttachments,
    neighbors,
}: {
    area: GrowerArea
    landAttachments?: AttachmentsFieldDraft
    neighbors: {
        growerId?: string
        excludeAreaIds: string[]
        siblings: AreaMapItem[]
    }
}) {
    const [view, setView] = useState<AreaView>('map')
    const geo = normalizeGeo(area.paths, area.markers)
    // Màn chỉ xem: vẫn đọc camera thật, nhưng không cho thêm/sửa/điều khiển.
    const { data: cameras = [] } = useGrowerAreaCameras(area.id)

    return (
        <div className="grid gap-5 md:grid-cols-[1.5fr_1fr]">
            <div className="space-y-2.5">
                {/* Vùng cũ chưa có id thì không gắn được camera — không dựng
                    nút chuyển để khỏi mở ra một khung luôn rỗng. */}
                {area.id && (
                    <AreaViewToggle
                        view={view}
                        onChange={setView}
                        cameraCount={cameras.length}
                    />
                )}
                {!area.id || view === 'map' ? (
                    <GrowerAreaMap
                        value={geo}
                        onChange={() => {}}
                        readOnly
                        fitKey={area.id ?? area.code}
                        neighbors={neighbors}
                    />
                ) : (
                    <AreaCameraPanel
                        areaId={area.id}
                        height={AREA_VIEW_H}
                        canWrite={false}
                    />
                )}
            </div>

            <div className="space-y-3.5">
                <ReadonlyField
                    label={m.grower_area_name()}
                    value={area.name}
                    testId="grower-area-name"
                />
                <ReadonlyField
                    label={m.grower_area_acreage()}
                    value={
                        area.acreage
                            ? `${area.acreage.toLocaleString('vi-VN')} m²`
                            : undefined
                    }
                />
                <ReadonlyField
                    label={m.grower_area_note()}
                    value={area.note}
                    className="whitespace-pre-line"
                />
                <div>
                    <Label className="mb-1.5 block">
                        {m.grower_coordinates()}
                    </Label>
                    <AreaCoordTable geo={geo} onChange={() => {}} readOnly />
                </div>
                {landAttachments && (
                    <div className="space-y-1.5">
                        <Label>{m.grower_land_documents()}</Label>
                        <GrowerAttachmentsField
                            draft={landAttachments}
                            readOnly
                            typesHint={m.grower_land_documents_hint()}
                            label={m.grower_land_documents()}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}
